import { ApplicationRef, Injectable } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { Capacitor } from '@capacitor/core';
import { concat, filter, first, interval, race, take, timer } from 'rxjs';
import { LoggerService } from './logger.service';

const UPDATE_CHECK_INTERVAL_MS = 60 * 60 * 1000;
const APP_STABLE_TIMEOUT_MS = 30 * 1000;

@Injectable({
  providedIn: 'root',
})
export class AppUpdateService {
  private logger = new LoggerService(AppUpdateService.name);

  constructor(
    private readonly swUpdate: SwUpdate,
    private readonly appRef: ApplicationRef,
  ) {}

  initialize(): void {
    if (Capacitor.isNativePlatform()) {
      return;
    }

    if (!this.swUpdate.isEnabled) {
      return;
    }

    this.subscribeToVersionUpdates();
    this.subscribeToUnrecoverableState();
    this.schedulePeriodicChecks();
  }

  private subscribeToUnrecoverableState(): void {
    this.swUpdate.unrecoverable.pipe(take(1)).subscribe((event) => {
      this.logger.warn({
        message: 'Service worker in unrecoverable state, reloading',
        identifier: 'app_update_service_unrecoverable_reload',
        method: 'subscribeToUnrecoverableState',
        value: event.reason,
      });

      document.location.reload();
    });
  }

  private subscribeToVersionUpdates(): void {
    this.swUpdate.versionUpdates
      .pipe(
        filter(
          (event): event is VersionReadyEvent => event.type === 'VERSION_READY',
        ),
        take(1),
      )
      .subscribe(async (event) => {
        this.logger.info({
          message: 'New app version is ready, reloading',
          identifier: 'app_update_service_version_ready_reload',
          method: 'subscribeToVersionUpdates',
          value: {
            current: (event.currentVersion as { hash?: string })?.hash,
            latest: (event.latestVersion as { hash?: string })?.hash,
          },
        });

        try {
          await this.swUpdate.activateUpdate();
        } catch (error) {
          this.logger.warn({
            message: 'Failed to activate SW update before reload',
            identifier: 'app_update_service_activate_update_failed',
            method: 'subscribeToVersionUpdates',
            value: (error as Error)?.message ?? String(error),
          });
        }

        document.location.reload();
      });
  }

  private schedulePeriodicChecks(): void {
    // isStable never emits true while anything keeps scheduling macrotasks
    // (polling, analytics timers), which would block the periodic checks
    // forever — fall back to a timeout like registerWhenStable:30000 does.
    const appStable$ = race(
      this.appRef.isStable.pipe(first((stable) => stable)),
      timer(APP_STABLE_TIMEOUT_MS),
    ).pipe(take(1));
    const periodic$ = interval(UPDATE_CHECK_INTERVAL_MS);

    concat(appStable$, periodic$).subscribe(() => {
      this.swUpdate.checkForUpdate().catch((error) => {
        this.logger.warn({
          message: 'Service worker update check failed',
          identifier: 'app_update_service_check_failed',
          method: 'schedulePeriodicChecks',
          value: (error as Error)?.message ?? String(error),
        });
      });
    });
  }
}
